import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TeleScript from '../utils/TeleScript';
import DisplayContent from '../utils/DisplayContent';
import { EntryContext } from '../contexts/entryContext';
import useIGRGameUrl from '../hooks/useIGRGameUrl';
import TagManager from 'react-gtm-module';
import ERROR_MESSAGES from '../contants/errorConstants';
import BirthDateSelector from './BirthDateSelector';

const AgeVerification = () => {
    const navigate = useNavigate();
    const { game, revealGames, entry, siteAssets, setIgrGameUrl, setSelectedRevealGame, setErrorCode } = useContext(EntryContext);
    const [isUnder18, setIsUnder18] = React.useState(false);
    const [dobVerified, setDobVerified] = React.useState(false);

    const selectedRevealGame = revealGames?.find((rg) => rg.game_id === entry?.TicketDetails?.RevealGameId) || null;

    const canDisplayContinueSecton = !game?.gameIsClosed && (!siteAssets?.features?.birthDateSelector || dobVerified);

    const [fetchUrlTrigger, setFetchUrlTrigger] = useState(false);
    const [shouldNavigate, setShouldNavigate] = useState(false);

    const { gameUrl, error } = useIGRGameUrl(fetchUrlTrigger ? selectedRevealGame : null);

    useEffect(() => {
        if (shouldNavigate && gameUrl) {
            // Analytics event tracking for game result screen
            const tagManagerArgs = {
                dataLayer: {
                    event: 'rewatch_game',
                    category: 'GAME_REWATCHED',
                    action: `GAME_RESULT SCREEN_${selectedRevealGame?.tag}`,
                },
            };
            TagManager.dataLayer(tagManagerArgs);

            setIgrGameUrl(gameUrl);
            navigate(`/play`, { replace: true });

            setShouldNavigate(false);
            setFetchUrlTrigger(false);
        }
    }, [shouldNavigate, gameUrl, selectedRevealGame?.tag, setIgrGameUrl, navigate]);

    useEffect(() => {
        if (error) {
            //? ERROR_IGR_GAME_URL_NOT_RETURNED: The IGR game URL was not returned from the IGR service
            console.error('🔴 Error fetching game URL:', error);
            setErrorCode(ERROR_MESSAGES.IGR_GAME_URL_NOT_RETURNED);
            setFetchUrlTrigger(false);
            navigate(`/`, { replace: true });
        }
    }, [error, setErrorCode, navigate]);

    useEffect(() => {
        if (error) {
            setFetchUrlTrigger(false);
            return navigate(`/`, { replace: true });
        }
    }, [error, navigate]);

    const clickHandler = (event) => {
        event.preventDefault();

        // Already played the reveal game: players go directly to the results page
        if (entry?.TicketDetails?.RevealGameId && selectedRevealGame) {
            setSelectedRevealGame(selectedRevealGame);
            setShouldNavigate(true);
            setFetchUrlTrigger(true);
        }
        // Reveal game already played but not found in the CMS
        else if (entry?.TicketDetails?.RevealGameId && !selectedRevealGame) {
            console.error(`🔴 Reveal game already played but not found in the CMS: ${entry?.TicketDetails?.RevealGameId}`);
            //? ERROR_INVALID_REVEAL_GAME_ID: Reveal game already played but not found in the CMS
            setErrorCode(ERROR_MESSAGES.INVALID_REVEAL_GAME_ID);
            setFetchUrlTrigger(false);
            navigate(`/`, { replace: true });
            return;
        }
        // First scan: players go to the game selection page
        else {
            return navigate(`/games`, { replace: true });
        }
    };

    const setUnder18 = (event) => {
        event.preventDefault();
        setIsUnder18(true);
    };

    return (
        <div id='age-verification-component' className='age-info'>
            <DisplayContent note='Age Verification Image' isVisible={siteAssets?.ageVerificationLogoImage?.ref}>
                <div id='age-verification-logo-image' className='age-logo-img'>
                    <img src={siteAssets?.ageVerificationLogoImage?.ref} alt={siteAssets?.ageVerificationLogoImage?.alt} />
                </div>
            </DisplayContent>
            <DisplayContent note='Age Verification Image' isVisible={siteAssets?.ageVerificationTextImage?.ref}>
                <div id='age-verification-text-image' className='age-text-img'>
                    <img
                        key={`age-verification-text-image-under-18-${isUnder18}`}
                        src={isUnder18 ? siteAssets?.ageVerificationMustBeOver18TextImage?.ref : siteAssets?.ageVerificationTextImage?.ref}
                        alt={isUnder18 ? siteAssets?.ageVerificationMustBeOver18TextImage?.alt : siteAssets?.ageVerificationMustBeOver18TextImage?.alt}
                    />
                </div>
            </DisplayContent>
            <DisplayContent note='Only show content for users over 18' isVisible={!isUnder18}>
                <DisplayContent note='description is optional' isVisible={game?.ageVerificationDescription}>
                    <div id='age-verification-description-container' className='age-description-container'>
                        <div id='age-verification-description' className='age-description'>
                            <TeleScript line={game?.ageVerificationDescription}>Description content is needed.</TeleScript>
                        </div>
                    </div>
                </DisplayContent>

                <DisplayContent note='DOB Date Picker to verify your age' isVisible={siteAssets?.features?.birthDateSelector}>
                    <BirthDateSelector setDobVerified={setDobVerified} />
                </DisplayContent>

                <DisplayContent note='Show or Hide Continue Section' isVisible={canDisplayContinueSecton}>
                    {siteAssets?.features?.ageVerificationContinueEnabled ? (
                        <DisplayContent note='continue button' isVisible={siteAssets?.ageVerificationContinueButtonImage?.ref}>
                            <div className='age-confirm'>
                                <input
                                    tabIndex='0'
                                    type='image'
                                    alt={siteAssets?.ageVerificationContinueButtonImage?.alt}
                                    src={siteAssets?.ageVerificationContinueButtonImage?.ref}
                                    onClick={clickHandler}
                                />
                            </div>
                        </DisplayContent>
                    ) : (
                        <DisplayContent
                            note='yes and no buttons'
                            isVisible={siteAssets?.ageVerificationYesButtonImage?.ref && siteAssets?.ageVerificationNoButtonImage?.ref}>
                            <div style={{ display: 'flex', gap: '18px' }}>
                                <div className='age-confirm'>
                                    <input
                                        tabIndex='0'
                                        type='image'
                                        alt={siteAssets?.ageVerificationYesButtonImage?.alt}
                                        src={siteAssets?.ageVerificationYesButtonImage?.ref}
                                        onClick={clickHandler}
                                    />
                                </div>
                                <div className='age-confirm'>
                                    <input
                                        tabIndex='0'
                                        type='image'
                                        alt={siteAssets?.ageVerificationNoButtonImage?.alt}
                                        src={siteAssets?.ageVerificationNoButtonImage?.ref}
                                        onClick={setUnder18}
                                    />
                                </div>
                            </div>
                        </DisplayContent>
                    )}
                </DisplayContent>
            </DisplayContent>
        </div>
    );
};

export default AgeVerification;
